import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { saveAs } from "file-saver";
import { deletePost } from "../../functions/post";

// If REACT_APP_API_URL is set to empty string, use relative URLs (same domain)
// Otherwise use the provided URL or default to localhost for development
const API_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : "http://localhost:8000";

export default function PostMenu({
  postUserId,
  userId,
  imagesLength,
  setShowMenu,
  token,
  postId,
  checkSaved,
  setCheckSaved,
  images,
  postRef,
  onPostDeleted,
  savedViewMenu = false,
  onUnsave,
}) {
  const menu = useRef(null);
  const fullscreenRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [, setIsDeleting] = useState(false);
  const [isPinned, setIsPinned] = useState(false); // Track pin state
  const [isArchived, setIsArchived] = useState(false); // Track archive state
  const [showMoveToBinConfirm, setShowMoveToBinConfirm] = useState(false);
  // Report state
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const reportReasons = [
    { key: "sexual_content", label: "Sexual content" },
    { key: "violent_or_repulsive", label: "Violent or repulsive content" },
    { key: "hateful_or_abusive", label: "Hateful or abusive content" },
    { key: "harassment_or_bullying", label: "Harassment or bullying" },
    { key: "harmful_or_dangerous", label: "Harmful or dangerous acts" },
    { key: "self_harm_or_eating_disorders", label: "Suicide, self-harm, or eating disorders" },
    { key: "child_safety", label: "Child safety" },
    { key: "misinformation", label: "Misinformation" },
    { key: "spam_or_scams", label: "Spam or scams" },
    { key: "copyright_or_trademark", label: "Copyright or trademark" },
    { key: "privacy", label: "Privacy" },
    { key: "other", label: "Other" },
  ];
  const [selectedReasons, setSelectedReasons] = useState([]);

  // Check if current user is the post owner
  const test = postUserId === userId;

  // Fetch pin state from backend when component mounts
  React.useEffect(() => {
    async function fetchPinState() {
      if (!token || !postId) {
        return; // Don't fetch if token or postId is missing
      }
      
      try {
        const response = await fetch(`${API_URL}/getPost/${postId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          // Log error but don't show to user (non-critical feature)
          console.warn(`Failed to fetch post state: ${response.status} ${response.statusText}`);
          return;
        }
        
        const data = await response.json();
        if (data && typeof data.pinned === 'boolean') {
          setIsPinned(data.pinned);
        }
        // initialize archive state if available
        if (data && typeof data.archived === 'boolean') {
          setIsArchived(data.archived);
        }
      } catch (err) {
        // Log error but don't show to user (non-critical feature)
        console.warn('Error fetching post pin/archive state:', err.message);
      }
    }
    fetchPinState();
  }, [postId, token]);

  // Handler for pin/unpin post
  const handlePinToggle = async () => {
    try {
      // Call backend API to pin/unpin post (replace endpoint as needed)
      const response = await fetch(`${API_URL}/pinPost/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pin: !isPinned }),
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setIsPinned(!isPinned);
        // Show toast
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">${data.message || (isPinned ? 'Publication unpinned' : 'Publication pinned')}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 4000);
      } else {
        throw new Error('Failed to pin/unpin post');
      }
    } catch (error) {
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">${error.message || 'Failed to pin/unpin publication.'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 4000);
    }
  };

  // Handler for archive/unarchive post
  const handleArchiveToggle = async () => {
    try {
      const res = await fetch(`${API_URL}/archivePost/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ archive: !isArchived }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'ok') {
        setIsArchived(!isArchived);
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">${data.message || (isArchived ? 'Publication restored from archive' : 'Publication archived')}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => { if (document.body.contains(toast)) document.body.removeChild(toast); }, 4000);
        setShowMenu(false);
        
        // Trigger a page refresh or callback to update the feed
        if (onPostDeleted && typeof onPostDeleted === 'function') {
          setTimeout(() => {
            onPostDeleted(postId, { archived: !isArchived });
          }, 500);
        }
      } else {
        throw new Error(data.message || 'Failed to archive/unarchive post');
      }
    } catch (error) {
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">${error.message || 'Failed to archive post'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => { if (document.body.contains(errorToast)) document.body.removeChild(errorToast); }, 4000);
    }
  };

  // Close menu when clicked outside
  const useOnClickOutside = (ref, handler) => {
    React.useEffect(() => {
      const listener = (event) => {
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener("mousedown", listener);
      return () => {
        document.removeEventListener("mousedown", listener);
      };
    }, [ref, handler]);
  };

  useOnClickOutside(menu, (e) => {
    // If a modal is open, ignore outside clicks so the modal stays mounted
    if (showReport || showMoveToBinConfirm) return;
    setShowMenu(false);
  });

  const saveHandler = async () => {
    try {
      const response = await fetch(`${API_URL}/savePost/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === 'ok') {
        // Update the save status
        setCheckSaved((prev) => !prev);
        
        // Professional toast notification
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">${data.message}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 4000);
        
        // Close the menu
        setShowMenu(false);
        // When in saved view and user unsaved, notify parent to close fullscreen and refresh list
        if (savedViewMenu && checkSaved && typeof onUnsave === 'function') {
          onUnsave(postId);
        }
      } else {
        const errorToast = document.createElement('div');
        errorToast.className = 'error_toast';
        errorToast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">❌</span>
            <span class="toast_message">Failed to save/unsave post</span>
          </div>
        `;
        document.body.appendChild(errorToast);
        setTimeout(() => {
          if (document.body.contains(errorToast)) {
            document.body.removeChild(errorToast);
          }
        }, 4000);
      }
    } catch (error) {
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">Error saving/unsaving post</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 4000);
    }
  };

  const downloadImages = async () => {
    if (images && images.length > 0) {
      try {
        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          try {
            // Create a proper filename with timestamp and index
            const timestamp = new Date().getTime();
            const fileExtension = img.url.split('.').pop()?.split('?')[0] || 'jpg';
            const filename = `image_${timestamp}_${i + 1}.${fileExtension}`;
            
            // Fetch the image with proper headers
            const response = await fetch(img.url, {
              method: 'GET',
              headers: {
                'Accept': 'image/*',
              },
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            saveAs(blob, filename);
            
            // Show success toast for each download
            const successToast = document.createElement('div');
            successToast.className = 'success_toast';
            successToast.innerHTML = `
              <div class="toast_content">
                <span class="toast_icon">✅</span>
                <span class="toast_message">Downloaded ${filename}</span>
              </div>
            `;
            document.body.appendChild(successToast);
            setTimeout(() => {
              if (document.body.contains(successToast)) {
                document.body.removeChild(successToast);
              }
            }, 2000);
            
            // Add a small delay between downloads to avoid overwhelming the browser
            if (i < images.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(`Failed to download image ${i + 1}:`, error);
            // Show user-friendly error message
            const errorToast = document.createElement('div');
            errorToast.className = 'error_toast';
            errorToast.innerHTML = `
              <div class="toast_content">
                <span class="toast_icon">❌</span>
                <span class="toast_message">Failed to download image ${i + 1}</span>
              </div>
            `;
            document.body.appendChild(errorToast);
            setTimeout(() => {
              if (document.body.contains(errorToast)) {
                document.body.removeChild(errorToast);
              }
            }, 3000);
          }
        }
      } catch (error) {
        console.error('Download error:', error);
        const errorToast = document.createElement('div');
        errorToast.className = 'error_toast';
        errorToast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">❌</span>
            <span class="toast_message">Failed to download images</span>
          </div>
        `;
        document.body.appendChild(errorToast);
        setTimeout(() => {
          if (document.body.contains(errorToast)) {
            document.body.removeChild(errorToast);
          }
        }, 3000);
      }
    }
  };

  // Simple test function to debug
  const testBackendConnection = async () => {
    try {
      // console.log("Testing backend connection...");
      const response = await fetch(`${API_URL}/getAllPosts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // console.log("Backend test response:", response.status, response.ok);
      if (response.ok) {
        // console.log("✅ Backend is accessible");
      } else {
        // console.log("❌ Backend returned error:", response.status);
      }
    } catch (error) {
      // console.log("❌ Backend connection failed:", error.message);
    }
  };

  const handleDelete = async () => {
    // Test backend connection first
    await testBackendConnection();
    
    setIsDeleting(true);
    
    try {
      const res = await deletePost(postId, token);
      
      if (res && (res.status === "ok" || res.message)) {
        // Show success toast notification
        const successMessage = `Publication deleted successfully!`;
        
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">${successMessage}</span>
          </div>
        `;
        document.body.appendChild(toast);
        
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 5000);
        
        setShowMenu(false);

        // Let React remove the post from state — do not mutate/remove DOM
        // (that left residual hide styles on the next post when keys were indices)
        if (onPostDeleted && typeof onPostDeleted === 'function') {
          onPostDeleted(postId);
        }
        
      } else {
        throw new Error("Delete operation did not return success status");
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      
      // Show error toast
      const errorMessage = error.message || "Failed to delete post. Please try again.";
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">${errorMessage}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 4000);
    } finally {
      setIsDeleting(false);
    }
  };

  const enterFullscreen = () => {
    const fullscreenElement = fullscreenRef.current;
    if (fullscreenElement.requestFullscreen) {
      fullscreenElement.requestFullscreen();
    } else if (fullscreenElement.webkitRequestFullscreen) {
      fullscreenElement.webkitRequestFullscreen(); // Safari
    } else if (fullscreenElement.msRequestFullscreen) {
      fullscreenElement.msRequestFullscreen(); // IE11
    }
    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen(); // Safari
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen(); // IE11
    }
    setIsFullscreen(false);
  };

  // Handler for blocking a user
  const handleBlockUser = async () => {
    if (!postUserId || postUserId === userId) return;
    try {
      // Call backend API to block the user
      const response = await fetch(`${API_URL}/api/block/blockUser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: postUserId }),
      });
      if (response.ok) {
        // Optionally, update UI state, remove posts, etc.
        // Show success toast
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">User blocked successfully. Their posts will be hidden and they are added to your block list.</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 4000);
        setShowMenu(false);
        // Optionally, trigger a refresh or callback to update feed and settings
        if (onPostDeleted && typeof onPostDeleted === 'function') {
          onPostDeleted(postUserId, { block: true });
        }
      } else {
        throw new Error('Failed to block user');
      }
    } catch (error) {
      // Show error toast
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">${error.message || 'Failed to block user.'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 4000);
    }
  };

  const submitReport = async () => {
    if ((!reportReason && selectedReasons.length === 0)) {
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">Please select at least one reason to continue.</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => document.body.contains(errorToast) && document.body.removeChild(errorToast), 3000);
      return;
    }
    try {
      const payload = selectedReasons.length ? { reasons: selectedReasons, details: reportDetails } : { reason: reportReason, details: reportDetails };
      const res = await fetch(`${API_URL}/api/posts/${postId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.status === 'ok') {
        const toast = document.createElement('div');
        toast.className = 'success_toast';
        toast.innerHTML = `
          <div class="toast_content">
            <span class="toast_icon">✅</span>
            <span class="toast_message">Report submitted. Thank you for helping keep the community safe.</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.contains(toast) && document.body.removeChild(toast), 4000);
        setShowReport(false);
        setReportReason("");
        setReportDetails("");
        setSelectedReasons([]);
      } else {
        throw new Error(data.message || 'Failed to submit report');
      }
    } catch (error) {
      const errorToast = document.createElement('div');
      errorToast.className = 'error_toast';
      errorToast.innerHTML = `
        <div class="toast_content">
          <span class="toast_icon">❌</span>
          <span class="toast_message">${error.message || 'Failed to submit report'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => document.body.contains(errorToast) && document.body.removeChild(errorToast), 4000);
    }
  };

  // Preload user's previous report to mark selection
  React.useEffect(() => {
    const fetchMyReport = async () => {
      if (!showReport || !postId || !token) return;
      try {
        const res = await fetch(`${API_URL}/api/posts/${postId}/report`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.report) {
          if (Array.isArray(data.report.reasons) && data.report.reasons.length) {
            setSelectedReasons(data.report.reasons);
            setReportReason("");
          } else if (data.report.reason) {
            setReportReason(data.report.reason);
            setSelectedReasons([]);
          }
          setReportDetails(data.report.details || "");
        }
      } catch (e) {
        // ignore
      }
    };
    fetchMyReport();
  }, [showReport, postId, token]);

  // Toggle a checkbox reason
  const toggleReason = (key) => {
    setSelectedReasons((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  return (
    <div>
      {/* Move to bin confirmation modal — portal to body so mobile .post:active transform doesn't trap position:fixed */}
      {showMoveToBinConfirm &&
        createPortal(
          <div
            className="modal-overlay"
            onClick={(e) => {
              if (e.target !== e.currentTarget) return;
              setShowMoveToBinConfirm(false);
            }}
          >
            <div
              className="modal move_to_bin_modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="move-to-bin-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal_header">
                <h3 id="move-to-bin-title">Move to your bin?</h3>
                <button
                  type="button"
                  className="modal_close_btn"
                  onClick={() => setShowMoveToBinConfirm(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div className="modal_body">
                <p className="move_to_bin_info">
                  Deleted items are stored in the Recycle Bin for 30 days. You can permanently delete them anytime.
                </p>
              </div>
              <div className="modal_footer">
                <button
                  type="button"
                  className="btn light"
                  onClick={() => setShowMoveToBinConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMoveToBinConfirm(false);
                    setShowMenu(false);
                    handleDelete();
                  }}
                >
                  Move
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Report modal */}
      {showReport && (
        <div className="modal-overlay" onMouseDown={() => setShowReport(false)} onClick={() => {}}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal_header">
              <h3>Report</h3>
            </div>
            <div className="modal_body">
              <p>What's going on? You can select multiple categories.</p>
              <div className="report_list">
                {reportReasons.map((r) => {
                  const checked = selectedReasons.includes(r.key) || (!selectedReasons.length && reportReason === r.key);
                  return (
                    <label key={r.key} className={`report_item ${checked ? 'selected' : ''}`} onMouseDown={(e) => e.preventDefault()}>
                      <input
                        type="checkbox"
                        name="report_reason"
                        value={r.key}
                        checked={checked}
                        onChange={() => toggleReason(r.key)}
                      />
                      <span>{r.label}</span>
                    </label>
                  );
                })}
              </div>
              <textarea
                placeholder="Add more details (optional)"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                rows={3}
                style={{ width: '100%', marginTop: 10 }}
              />
            </div>
            <div className="modal_footer">
              <button type="button" className="btn light" onClick={() => setShowReport(false)}>Cancel</button>
              <button type="button" className="btn primary" disabled={selectedReasons.length === 0 && !reportReason} onClick={(e) => { e.preventDefault(); e.stopPropagation(); submitReport(); }}>Submit</button>
            </div>
          </div>
        </div>
      )}

      <div
        ref={fullscreenRef}
        className={`fullscreen-container ${isFullscreen ? "active" : ""}`}
      >
        {isFullscreen && (
          <button className="fullscreen-close" onClick={exitFullscreen}>
            &times;
          </button>
        )}
        {images && images.length > 0 && (
          <img
            src={images[0].url}
            alt="Post media"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        )}
      </div>
      <ul className="post_menu" ref={menu}>
        {savedViewMenu ? (
          <>
            <div onClick={saveHandler}>
              <MenuItem icon="save_icon" title="Unsave Publication" />
            </div>
            <div onClick={handlePinToggle}>
              <MenuItem icon="pin_icon" title={isPinned ? "Unpin Publication" : "Pin Publication"} />
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowMoveToBinConfirm(true);
              }}
            >
              <MenuItem icon="trash_icon" title="Delete Publication" />
            </div>
          </>
        ) : (
          <>
            {test && (
              <div onClick={handlePinToggle}>
                <MenuItem icon="pin_icon" title={isPinned ? "Unpin Publication" : "Pin Publication"} />
              </div>
            )}
            {test && (
              <div onClick={handleArchiveToggle}>
                <MenuItem icon="archive_icon" title={isArchived ? "Restore Publication" : "Archive Publication"} />
              </div>
            )}
            <div onClick={saveHandler}>
              {checkSaved ? (
                <MenuItem icon="save_icon" title="Unsave Publication" />
              ) : (
                <MenuItem icon="save_icon" title="Save Publication" />
              )}
            </div>
            {/* <div className="line"></div>
            {!test && (
              <MenuItem
                icon="turnOnNotification_icon"
                title="Turn on notifications for this Publication"
              />
            )} */}
            {imagesLength > 0 && (
              <div onClick={downloadImages}>
                <MenuItem icon="download_icon" title="Download" />
              </div>
            )}
            {imagesLength > 0 && (
              <div onClick={enterFullscreen}>
                <MenuItem icon="fullscreen_icon" title="Enter Fullscreen" />
              </div>
            )}
            {!test && (
              <div onClick={handleBlockUser}>
                <MenuItem
                 img="../../../icons/block-user.png"
                //  icon="block_icon"
                  title="Block User" />
              </div>
            )}
            {test && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMoveToBinConfirm(true);
                }}
              >
                <MenuItem icon="trash_icon" title="Move to trash" />
              </div>
            )}
            {!test && <div className="line"></div>}
            {!test && (
              <div onClick={() => setShowReport(true)}>
                <MenuItem
                  img="../../../icons/report.png"
                  title="Report publication"
                  subtitle="I'm concerned about this Publication"
                />
              </div>
            )}
          </>
        )}
      </ul>
    </div>
  );
}

function MenuItem({ icon, title, subtitle, img }) {
  return (
    <li className="menu-item">
      {img ? (
        <img src={img} alt={title} />
      ) : (
        <i className={`menu-item-icon ${icon}`}></i>
      )}
      <div className="menu-item-info">
        <span>{title}</span>
        {subtitle && <small>{subtitle}</small>}
      </div>
    </li>
  );
}
